import { inject, Injectable } from '@angular/core';
import { groupBy, mapValues } from 'lodash';
import { forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import { AssistantChatReportLink } from '../../interfaces/assistant-chat.interface';
import { CHIP_FILTER_OPERATORS } from '../../constants/query-filter.constants';
import { Query } from '../../models/analytics-api/query.model';
import { Match } from '../../models/analytics-api/search_result.model';
import { Schema } from '../../models/analytics-api/schema.model';
import { QueryFilter } from '../../models/query-filter.model';
import { DatesService } from '../../modules/date-range-picker/dates.service';
import { AnalyticsApiService } from '../analytics-api/analytics-api.service';
import { CrossAppNavigationService, OpenSettled } from '../cross-app-navigation/cross-app-navigation.service';

function decoded(value: string, term: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    throw new Error(`The report cannot apply the filter ${term}`);
  }
}

const LISTS = ['any', 'not_any'];

export function filtersOf(selection: string, schema: Schema): QueryFilter[][] {
  return selection
    .split(',')
    .filter(term => term.trim())
    .flatMap(term => {
      const [, key, type, values] = /^([^:]+):([^:]+):(.*)$/.exec(term.trim()) ?? [];
      const [dimensionName, keyProperty] = (key ?? '').split('.');
      const propertyName = keyProperty === dimensionName ? undefined : keyProperty;
      const dimension = schema.fetchDimension(dimensionName);
      const property = propertyName ? dimension?.properties?.find(candidate => candidate.name === propertyName) : undefined;
      if (!dimension || (propertyName && !property)) {
        throw new Error(`The report cannot apply the filter ${term}`);
      }
      const read = values.split(';').map(value => decoded(value, term));
      const named = read.filter(Boolean);
      const on = { key, dimension, ...(property ? { dimension_property: property } : {}) };
      if (CHIP_FILTER_OPERATORS.includes(type)) {
        if (named.length < read.length) {
          throw new Error(`The report cannot apply the filter ${term}`);
        }
        return [[new QueryFilter({ type, values: named, ...on })]];
      }
      const listed = new QueryFilter({ type, values: named.map(value => new Match(value, value, [])), ...on });
      if (!LISTS.includes(type) || named.length === read.length) {
        return [[listed]];
      }
      const kept = named.length ? [listed] : [];
      if (type === 'not_any') {
        return [kept, [new QueryFilter({ type: 'not_na', values: [], ...on })]].filter(group => group.length);
      }
      return [[...kept, new QueryFilter({ type: 'na', values: [], ...on })]];
    });
}

function dimensionPropertiesOf(properties: string[]): Record<string, string[]> {
  const pairs = properties.map(named => [named.slice(0, named.indexOf('.')), named.slice(named.indexOf('.') + 1)]);
  return mapValues(groupBy(pairs, ([dimension]) => dimension), grouped => grouped.map(([, property]) => property));
}

export function queryOf(link: AssistantChatReportLink, schema: Schema): Query {
  const query = new Query(
    {
      dataset: link.dataset,
      dimensions: link.dimensions,
      metrics: link.metrics,
      rollup: link.rollup,
      dimension_properties: link.properties && dimensionPropertiesOf(link.properties),
    },
    schema,
  );
  query.filters = filtersOf(link.selection, schema).map(expressions => ({ dataset: link.dataset, expressions }));
  return query;
}

@Injectable({ providedIn: 'root' })
export class AssistantChatReportLinks {
  private readonly analyticsApi = inject(AnalyticsApiService);
  private readonly crossAppNavigation = inject(CrossAppNavigationService);
  private readonly datesService = inject(DatesService);

  public open(link: AssistantChatReportLink, settled?: OpenSettled): void {
    this.crossAppNavigation.openInReportsOnceBuilt(
      this.analyticsApi.loadSchema(link.dataset).pipe(
        map(schema => ({ query: queryOf(link, schema), dateRange: this.datesService.intervalToFixedDateRange(link.interval) })),
      ),
      settled,
    );
  }

  public openInStudio(links: AssistantChatReportLink[], settled?: OpenSettled): void {
    const [first] = links;
    const datasets = [...new Set(links.map(link => link.dataset))];
    this.crossAppNavigation.openInStudioOnceBuilt(
      forkJoin(datasets.map(dataset => this.analyticsApi.loadSchema(dataset))).pipe(
        map(schemas => ({
          columns: links.length === 1 ? 1 : 2,
          dateRange: this.datesService.intervalToFixedDateRange(first.interval),
          tables: links.map(link => ({
            query: queryOf(link, schemas[datasets.indexOf(link.dataset)]),
            ...(link.interval === first.interval ? {} : { dateRange: this.datesService.intervalToFixedDateRange(link.interval) }),
          })),
        })),
      ),
      settled,
    );
  }
}
